import React, { FC } from 'react';
import styled from '@emotion/styled';
import {
  FontSecondarySemiBold,
  Icon,
  mediaQueries,
  spacingAround,
  spacingBottom,
  spacingRight,
  spacingTopBottom,
  readablePrice,
  FontSecondaryRegular,
  spacingCalculator
} from '@digando/react-component-library';
import { ContentContainerMax } from '../../layout/container';
import { ProductPageQuery } from '../../@types/codegen/graphql';
import { useTranslation } from 'next-i18next';
import { useProductLink, ProductPageLink } from './product-page-link';
import { LoginStates, useAuthContext } from '../../store/auth.context';
import { readableAddress } from '../../helper/readable-address';
import { useRouter } from 'next/router';
import { DrawerStep } from '../sidebar/interfaces/drawer.interfaces';
import { Select, Button } from '@digando/common-ui';
import { useSession } from 'next-auth/react';
import { useRecoilValue } from 'recoil';
import { lastSelectedProjectIdState } from '../../store/recoil/product-configurator';
import { ProductFees } from './product-fees';

interface IShortInfoProps {
  productBundle: ProductPageQuery['productBundle'];
  productPrice: ProductPageQuery['productPrice'];
}

const LoginHint: FC = () => {
  const [t] = useTranslation(['product-page']);
  const { setIsSignInVisible, setSignInStep } = useAuthContext();

  return (
    <LoginHintWrapper>
      {t('product-page:price-login-hint')}
      <button onClick={(): void => {
        setSignInStep(DrawerStep.SIGN_IN);
        setIsSignInVisible(true);
      }}>
        {t('product-page:price-login-hint-link-text')}
      </button>
    </LoginHintWrapper>
  );
};

export const ShortInfo: FC<IShortInfoProps> = ({
  productBundle,
  productPrice,
}) => {
  const [t] = useTranslation(['product-page', 'checkout-page']);
  const bundle = productBundle?.bundle;

  const { getProductLinkParams } = useProductLink();
  const isDeliverable = !!productBundle?.bundle?.availableRentalStations.find((rentalStation) => true === rentalStation.shippingOptions.deliveryEnabled);
  const selectedRentalStation = bundle?.selectedRentalStation ?? null;
  const router = useRouter();
  const rentalStationId = router.query.rentalStationId as string | undefined;
  const { status } = useSession();
  const lastSelectedProjectId = useRecoilValue(lastSelectedProjectIdState);

  const options = productBundle?.bundle?.availableRentalStations.map((rentalStation) => ({
    key: rentalStation.id,
    value: rentalStation.id,
    name: rentalStation.name,
    description: readableAddress(rentalStation),
  })) ?? [];

  const minRentalDuration = productBundle?.bundle?.selectedRentalStation?.minimumRentalPeriodDays ?? 1;

  return (
    <>
      <MobileWrapper>
        <DeliveryAvailabilityWrapper>
          {isDeliverable && (
            <DeliveryInfo>
              <IconWrapper>
                <Icon icon='zustellung' size={'30px'} color={`var(--secondary-color)`} />
              </IconWrapper>
              <SecondLineWrapper>{t('product-page:short-info.delivery-possible')}</SecondLineWrapper>
            </DeliveryInfo>
          )}

          {0 < (bundle?.availableProducts ?? 0) && (
            <AvailabilityInfo>
              <IconWrapper>
                <Icon icon='Verfugbarkeit' size={'30px'} color={`var(--secondary-color)`} />
              </IconWrapper>
              <SecondLineWrapper>
                {2 <= (bundle?.availableProducts ?? 0) ? t('product-page:short-info.sufficient') : t('product-page:short-info.available')}
              </SecondLineWrapper>
            </AvailabilityInfo>
          )}
        </DeliveryAvailabilityWrapper>
      </MobileWrapper>

      <ShortInfoContainer>
        <ContentContainerMaxStyled>
          <RentalStationInfo>
            <Select
              id={'rentalStation-select'}
              name={'rentalStation-select'}
              options={options}
              onChange={function (id: string): void {
                const rentalStation = productBundle?.bundle?.availableRentalStations.find((rentalStation) => rentalStation.id === id);

                router.replace(getProductLinkParams({
                  href: location.pathname,
                  rentalStationId: id,
                  shippingOptions: rentalStation?.shippingOptions,
                }), undefined, { scroll: false });
              }}
              defaultValue={selectedRentalStation?.id}
              noOptionsText={''}
              noSelectionText={''}
              bordered={true}
            />
          </RentalStationInfo>


          <DesktopWrapper>
            {isDeliverable && (
              <DeliveryInfo>
                <IconWrapper>
                  <Icon icon='zustellung' size={'30px'} color={`var(--secondary-contrast-font-color)`} />
                </IconWrapper>
                <SecondLineWrapper>{t('product-page:short-info.delivery-possible')}</SecondLineWrapper>
              </DeliveryInfo>
            )}

            {0 < (bundle?.availableProducts ?? 0) && (
              <AvailabilityInfo>
                <IconWrapper>
                  {/*eslint-disable-next-line @typescript-eslint/ban-ts-comment*/}
                  {/*@ts-ignore*/}
                  <Icon icon='Verfugbarkeit' size={'30px'} color={`var(--secondary-contrast-font-color)`} />
                </IconWrapper>
                <SecondLineWrapper>
                  {2 <= (bundle?.availableProducts ?? 0) ? t('product-page:short-info.sufficient') : t('product-page:short-info.available')}
                </SecondLineWrapper>
              </AvailabilityInfo>
            )}

          </DesktopWrapper>

          {selectedRentalStation && (
            <PriceInfo>
              <DailyPrice>
                {LoginStates.LOGGED_OUT === status && bundle?.selectedProduct?.hasHiddenPrices
                  ? <LoginHint />
                  : productPrice?.productPriceError && productPrice?.productPriceError.code === 'CalculationError'
                    ? <ErrorMessage>{t('checkout-page:price-calculation-error')}</ErrorMessage>
                    : (productPrice?.productPrice && (
                      <PriceWrapper>
                        {((productPrice?.productPrice.product.amountPerDay ?? 0) === (productPrice?.productPrice.product.amountPerDayReduced ?? 0))
                          ? <>{readablePrice(productPrice?.productPrice.product.amountPerDay ?? 0)} / {t('common:day')}</>
                          : <>
                            <OldPrice>statt {readablePrice(productPrice?.productPrice.product.amountPerDay ?? 0)} / {t('common:day')}</OldPrice>
                            {readablePrice(productPrice?.productPrice.product.amountPerDayReduced ?? 0)} / {t('common:day')}
                          </>
                        }
                      </PriceWrapper>
                    ))}
                {(1 < minRentalDuration) && (
                  <span>
                    {t('product-page:short-info.minimum-rental-duration', { amount: minRentalDuration })}
                  </span>
                )}
              </DailyPrice>
              <ProductFees serviceFees={productBundle?.bundle?.priceInformation?.serviceFees ?? []} />
            </PriceInfo>
          )}

          {selectedRentalStation && (
            <Action>
              {bundle && (
                <ProductPageLink href={bundle?.type.configuratorPageUrl} rentalStationId={rentalStationId} deliveryAddressId={lastSelectedProjectId} shippingOptions={bundle?.selectedRentalStation?.shippingOptions}>
                  <Button type={'button'}>
                    {t('product-page:short-info.short-info-button-title')}
                  </Button>
                </ProductPageLink>
              )}
            </Action>
          )}
        </ContentContainerMaxStyled>
      </ShortInfoContainer>
    </>
  );
};

const ErrorMessage = styled.div`
  max-width: 360px;
  text-align: center;

  @media ${mediaQueries.tablet} {
    text-align: left;
  }
`;

const DesktopWrapper = styled.div`
  display: none;

  @media ${mediaQueries.tablet} {
    grid-area: 2/1;
    ${spacingAround(0)};
    display: inline;
  }

  @media ${mediaQueries.laptop} {
    display: inherit;
    grid-area: 1/2;
  }
`;

const MobileWrapper = styled.div`
  @media ${mediaQueries.tablet} {
    display: none;
  }
`;

const IconWrapper = styled.div`
  ${spacingRight(2)};
  margin-bottom: 0;
  line-height: 0;

  @media ${mediaQueries.tablet} {
    ${spacingRight(1)};
  }
`;

const ShortInfoContainer = styled.div`
  --button-background-color-override: var(--configurator-cta-button-background-color);
  --button-font-color-override: var(--configurator-cta-button-font-color);
  --button-border-color-override: var(--configurator-cta-button-border-color);

  ${spacingTopBottom(3)};
  background-color: var(--secondary-background-color);
  color: var(--secondary-contrast-font-color);

  @media print {
    background-color: transparent;
    color: var(--black);
  }
`;

const ContentContainerMaxStyled = styled(ContentContainerMax)`
  grid-template-columns: 1fr;

  @media ${mediaQueries.tablet} {
    grid-template-columns: 1fr;
    grid-gap: ${spacingCalculator(2)} !important;
  }

  @media ${mediaQueries.laptop} {
    grid-template-columns: auto;
  }
`;

const DeliveryAvailabilityWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  ${spacingAround(4)};

  @media ${mediaQueries.tablet} {
    ${spacingAround(0)};
  }
`;

const SecondLineWrapper = styled.div`
  ${FontSecondaryRegular};
  font-size: var(--font-size-headline-h4);
  line-height: 24px !important;
  display: flex;
`;

const RentalStationInfo = styled.div`
  --select-option-text-color-override: var(--secondary-contrast-font-color);

  grid-column: auto / span 3;
  grid-area: 4/1;
  justify-self: center;
  ${spacingBottom(3)};
  width: 100%;

  @media ${mediaQueries.tablet} {
    justify-self: start;
    grid-area: 1/1;
    ${spacingAround(0)};
  }

  @media ${mediaQueries.laptop} {
    grid-area: 1/1;
    ${spacingAround(0)};
    align-self: center;
  }
`;

const DeliveryInfo = styled.div`
  display: flex;
  align-items: center;

  @media ${mediaQueries.tablet} {
    ${spacingRight(4)};
    ${spacingBottom(1)};
    display: flex;
    justify-self: start;
  }

  @media ${mediaQueries.laptop} {
    align-items: flex-start;
    display: grid;
    justify-self: start;
    grid-area: 1 /1;
    padding-bottom: 0;
    align-self: center;
  }
`;

const AvailabilityInfo = styled.div`
  display: flex;
  align-items: center;

  @media ${mediaQueries.tablet} {
    display: flex;
  }

  @media ${mediaQueries.laptop} {
    align-items: flex-start;
    display: grid;
    justify-self: center;
    grid-area: 1/2;
    align-self: center;
  }
`;

const DailyPrice = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacingCalculator(0.5)};
  align-self: center;

  span {
    white-space: nowrap;
  }
`

const PriceInfo = styled.div`
  grid-column: auto / span 2;
  margin-left: auto;
  margin-right: auto;
  grid-area: 2/1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacingCalculator(2)};

  @media ${mediaQueries.tablet} {
    grid-area: 1/2;
    align-items: flex-end;
  }

  @media ${mediaQueries.laptop} {
    margin-top: auto;
    margin-bottom: auto;
    margin-left: auto;
    margin-right: 0;
    grid-area: 1/3;
    align-self: center;
    flex-direction: row;
  }

  @media ${mediaQueries.laptop} {
    min-width: 140px;
  }
  @media ${mediaQueries.laptopL} {
    min-width: 170px;
  }
`;

const PriceWrapper = styled.div`
  ${FontSecondarySemiBold};
  font-size: var(--font-size-headline-h2);
  line-height: 38px;
  white-space: nowrap;

  @media ${mediaQueries.laptop} {
    font-size: 28px;
  }

  @media ${mediaQueries.laptopL} {
    font-size: var(--font-size-headline-h2);
    align-self: center;
  }
`;

const OldPrice = styled.div`
  ${FontSecondarySemiBold};
  font-size: 16px;
  line-height: 16px;
  text-align: right;
  text-decoration: line-through;
`;

const Action = styled.div`
  grid-column: auto / span 3;
  text-align: center;
  justify-self: center;
  grid-area: 3;
  width: 100%;
  ${spacingBottom(4)};

  button {
    width: 100%;
    max-width: 343px;
  }

  @media ${mediaQueries.tablet} {
    grid-area: 2/2;
    ${spacingBottom(0.5)};
    align-self: end;
    width: unset;
  }

  @media ${mediaQueries.laptop} {
    align-self: center;
    grid-area: 1/4;
    ${spacingAround(0)};
    align-self: center;
  }
`;

const LoginHintWrapper = styled.div`
  button {
    display: inline;
    background-color: transparent;
    border: none;
    padding: 0;
    margin: 0;
    color: var(--white);
    cursor: pointer;
    text-decoration: underline;
  }
`;
